import { ProposalInfoPage } from "@client/components/round/allocator/EvaluablePage/ProposalInfoPage";
import { RoundTitle } from "@client/components/round/RoundTitle";
import { Button } from "@client/components/utils/Button";
import Modal from "@client/components/utils/Modal";
import RecusedBadge from "@client/components/utils/RecusedBadge";
import { ExternalLinkIconInText } from "@client/icons/ExternalLinkIcon";
import { isEffectivelyRecused } from "@client/utils/interest";
import { RecentIndicator } from "@client/utils/recentProposals";
import userIsAdmin from "@client/utils/userIsAdmin";
import { getAllProposalForms } from "@engine/round/accessors";
import {
  Field,
  FieldResponse,
  Proposal,
  ProposalForm,
} from "@engine/round/aggregate";
import clsx from "clsx";
import { useContext, useState } from "react";
import { Redirect } from "wouter";
import { assertUnreachable } from "../../../../lib/utils/assertUnreachable";
import { DispatchContext, StateContext } from "../Round";
import { FormField } from "./ProposalsForm";

export function ProposalsPage() {
  const context = useContext(StateContext);
  const { round, actingAsUserId: userId } = context;
  const dispatch = useContext(DispatchContext);
  const viewerIsAdmin = userIsAdmin(userId, context);

  const [openedProposalAndForm, setOpenedProposalAndForm] = useState<{
    openedFor: "editing" | "viewing";
    proposal: Proposal;
    proposalForm: ProposalForm;
  } | null>(null);

  function openForEditing(proposalForm: ProposalForm, proposal: Proposal) {
    setOpenedProposalAndForm({ openedFor: "editing", proposalForm, proposal });
  }

  function openForViewing(proposalForm: ProposalForm, proposal: Proposal) {
    setOpenedProposalAndForm({ openedFor: "viewing", proposalForm, proposal });
  }

  function close() {
    setOpenedProposalAndForm(null);
  }

  function deleteProposal(proposalFormId: string, proposalId: string) {
    void dispatch({
      type: "REMOVE_PROPOSAL",
      proposalId,
      proposalFormId,
    });
  }

  /*
  function addProposal() {
    const proposalId = generateProposalId();
    const numProposals = Object.keys(proposalForm.proposals).length;
    const proposalName = `New Proposal ${numProposals + 1}`;
    void dispatch({
      type: "ADD_PROPOSAL",
      proposalFormId,
      proposalId: proposalId,
      proposalShortname: proposalName,
      proposalLongname: proposalName,
      proposalFirstSubmittedDatetime: new Date().toISOString(),
    });
  }
  */

  const proposalForms = getAllProposalForms(round);
  const thClass =
    "py-2 px-4 border-r border-y light:border-gray-200 dark:border-stone-700 light:bg-white dark:bg-stone-800 sticky top-0 ";

  if (!viewerIsAdmin) {
    return <Redirect to="/" />;
  }
  return (
    <div className="w-full m-8 overflow-x-auto">
      <RoundTitle />
      <table className="border-separate border-spacing-0">
        <thead>
          <tr className="light:bg-white dark:bg-stone-800">
            <th className={clsx(thClass, "sticky left-0 z-20 border-l")}>
              Proposal
            </th>
            <th className={thClass}>Form</th>
            <th className={thClass}>Entity Type</th>
            <th className={thClass}>Requests Matching Pledge</th>
            <th className={thClass}>Match Rate</th>
            <th className={thClass}>Baseline Standard Grant Amount</th>
            <th className={thClass}>Target Outside Donations</th>
            <th className={thClass}>Base Request Amount</th>
            <th className={thClass}>Min Grant Amount</th>
            <th className={thClass}>Max Grant Amount</th>
            <th className={thClass}>Receiving Org Name</th>
            <th className={thClass}>Receiving Org Tax ID</th>
            <th className={thClass}>Receiving Org Location</th>
            <th className={thClass}>SFF Application ID</th>
            <th className={thClass}>Longform Link</th>
            <th className={thClass}>Targets Freedom</th>
            <th className={thClass}>Targets Fairness</th>
            {viewerIsAdmin && <th className={thClass}>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {proposalForms.map((proposalForm) =>
            Object.values(proposalForm.proposals).map((proposal) => {
              if (proposal.id === round.holdProposalId) {
                return null;
              }
              return (
                <ProposalRow
                  proposalForm={proposalForm}
                  proposal={proposal}
                  openForViewing={openForViewing}
                  openForEditing={openForEditing}
                  isHold={proposal.id === round.holdProposalId}
                  deleteProposal={() =>
                    deleteProposal(proposalForm.id, proposal.id)
                  }
                  key={proposal.id}
                  viewerIsAdmin={viewerIsAdmin}
                />
              );
            }),
          )}
        </tbody>
      </table>
      {/*<Button onClick={addProposal}>Add</Button>*/}
      <OpenedProposalModal
        proposalAndForm={openedProposalAndForm}
        close={close}
      />
    </div>
  );
}

function niceFormName(formName: string) {
  if (formName === "SFF Rolling Application (real)") {
    return "Combined (real)";
  }
  if (formName === "SFF Rolling Application (testing)") {
    return "Combined (testing)";
  }
  if (formName === "SFF Charity Funding Rolling Application") {
    return "Charity (old)";
  }
  if (formName === "SFF for-profit non-dilutive grant application") {
    return "For-Profit (old)";
  }
  return formName;
}

function ProposalRow({
  proposalForm,
  proposal,
  openForViewing,
  openForEditing,
  isHold,
  deleteProposal,
  viewerIsAdmin,
}: {
  proposalForm: ProposalForm;
  proposal: Proposal;
  openForViewing: (proposalForm: ProposalForm, proposal: Proposal) => void;
  openForEditing: (proposalForm: ProposalForm, proposal: Proposal) => void;
  isHold: boolean;
  deleteProposal: (proposalId: string) => void;
  viewerIsAdmin: boolean;
}) {
  const tdClass =
    "py-2 px-4 border-b border-r light:border-gray-200 dark:border-stone-700 bg-white dark:bg-stone-800";

  let domain = "";
  try {
    domain = new URL(proposal.typedFields.longformLink).hostname;
  } catch {
    // Ignore (Failed to construct 'URL': Invalid URL is expected)
  }

  const entityType =
    proposal.entityType === "for-profit"
      ? "For-Profit"
      : proposal.entityType === "non-profit"
        ? "Non-Profit"
        : proposal.entityType === "social-welfare"
          ? "Social Welfare"
          : "Other";

  return (
    <tr className="light:border-gray-200 dark:border-stone-700">
      <th className={clsx(tdClass, "sticky left-0 z-10 border-l text-left")}>
        <button
          className="cursor-pointer"
          onClick={() => openForViewing(proposalForm, proposal)}
        >
          {proposal.shortname}
          <RecentIndicator proposalId={proposal.id} />
        </button>
      </th>
      <td className={tdClass}>{niceFormName(proposalForm.name)}</td>
      <td className={tdClass}>{entityType}</td>
      <td className={tdClass}>
        {proposal.requestsMatchingPledge ? "Yes" : "No"}
      </td>
      <td className={tdClass}>{proposal.typedFields.matchRate}</td>
      <td className={tdClass}>
        {proposal.typedFields.baselineStandardGrantAmount}
      </td>
      <td className={tdClass}>{proposal.typedFields.targetOutsideDonations}</td>
      <td className={tdClass}>{proposal.typedFields.baseRequestAmount}</td>
      <td className={tdClass}>{proposal.typedFields.minGrantAmount}</td>
      <td className={tdClass}>{proposal.typedFields.maxGrantAmount}</td>
      <td className={tdClass}>{proposal.typedFields.receivingOrgName}</td>
      <td className={tdClass}>{proposal.typedFields.receivingOrgTaxId}</td>
      <td className={tdClass}>{proposal.typedFields.receivingOrgLocation}</td>
      <td className={tdClass}>
        <pre>{proposal.typedFields.sffApplicationId}</pre>
      </td>
      <td className={tdClass}>
        {domain ? (
          <a
            href={proposal.typedFields.longformLink}
            target="_blank"
            rel="noreferrer noopener"
          >
            <span>{domain}/...</span>
            <ExternalLinkIconInText />
          </a>
        ) : (
          ""
        )}
      </td>
      <td className={tdClass}>
        {proposal.typedFields.targetsFreedom ? "Yes" : "No"}
      </td>
      <td className={tdClass}>
        {proposal.typedFields.targetsFairness ? "Yes" : "No"}
      </td>
      {viewerIsAdmin && (
        <td className={tdClass}>
          {!isHold && (
            <div className="flex gap-2">
              <Button onClick={() => deleteProposal(proposal.id)}>
                Delete
              </Button>
              <Button onClick={() => openForEditing(proposalForm, proposal)}>
                Edit
              </Button>
            </div>
          )}
        </td>
      )}
    </tr>
  );
}

function OpenedProposalModal({
  proposalAndForm,
  close,
}: {
  proposalAndForm: {
    openedFor: "editing" | "viewing";
    proposal: Proposal;
    proposalForm: ProposalForm;
  } | null;
  close: () => void;
}) {
  let children: React.ReactNode = null;
  const { round, actingAsUserId: userId } = useContext(StateContext);
  let recusedBadge: React.ReactNode = null;

  if (proposalAndForm != null) {
    const { proposal, proposalForm } = proposalAndForm;

    const disclosure = round.proposalDisclosure[`${proposal.id}%${userId}`];
    if (isEffectivelyRecused(disclosure)) {
      recusedBadge = (
        <div className="mb-2">
          <RecusedBadge />
        </div>
      );
    }

    children =
      proposalAndForm.openedFor === "editing" ? (
        <OpenedProposal
          proposal={proposal}
          proposalForm={proposalForm}
          close={close}
        />
      ) : (
        <ProposalInfoPage
          proposal={proposal}
          filteredProposalForm={proposalForm}
        />
      );
  }

  return (
    <Modal isOpen={proposalAndForm != null} onClose={close} fullHeight>
      {recusedBadge}
      {children}
    </Modal>
  );
}

function OpenedProposal({
  proposal,
  proposalForm,
  close,
}: {
  proposal: Proposal;
  proposalForm: ProposalForm;
  close: () => void;
}) {
  const dispatch = useContext(DispatchContext);

  const [longname, longnameSet] = useState(proposal.longname || "");
  const [longnameSaved, longnameSavedSet] = useState(true);
  const [shortname, shortnameSet] = useState(proposal.shortname || "");
  const [shortnameSaved, shortnameSavedSet] = useState(true);

  function onLongnameChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    const name = e.target.value;
    longnameSavedSet(name === proposal.longname);
    longnameSet(name);
  }

  function onShortNameChange(e: React.ChangeEvent<HTMLInputElement>) {
    const name = e.target.value;
    shortnameSavedSet(name === proposal.shortname);
    shortnameSet(name);
  }

  function shortnameSave() {
    void dispatch({
      type: "PROPOSAL_SHORTNAME_CHANGE",
      proposalId: proposal.id,
      proposalFormId: proposalForm.id,
      shortname,
    });

    shortnameSavedSet(true);
  }

  function longnameSave() {
    void dispatch({
      type: "PROPOSAL_LONGNAME_CHANGE",
      proposalId: proposal.id,
      proposalFormId: proposalForm.id,
      longname,
    });

    longnameSavedSet(true);
  }

  return (
    <div className="flex flex-col p-2 max-w-4xl mx-auto">
      <div className="flex justify-end">
        <Button onClick={close}>Close</Button>
      </div>
      <div className="flex items-center gap-2 mb-2">
        <div>Long Name:</div>
        <textarea
          className="dark:bg-[#333] border border-[#444] rounded px-1 py-1 text-xs focus:outline-none focus:border-[#2185d0] focus:shadow-[inset_0_-100px_0_rgba(33,133,208,0.15)]"
          value={longname}
          onChange={onLongnameChange}
        />
        {longnameSaved ? (
          ""
        ) : (
          <div>
            <Button onClick={longnameSave}>Save</Button>
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 mb-4">
        <div>Short Name:</div>
        <input
          className="dark:bg-[#333] border border-[#444] rounded px-1 py-1 text-xs focus:outline-none focus:border-[#2185d0] focus:shadow-[inset_0_-100px_0_rgba(33,133,208,0.15)]"
          value={shortname}
          type="text"
          onChange={onShortNameChange}
        />
        {shortnameSaved ? (
          ""
        ) : (
          <div>
            <Button onClick={shortnameSave}>Save</Button>
          </div>
        )}
      </div>
      <div className="flex flex-col gap-5">
        {proposalForm.fields.map((field, idx) => (
          <ProposalField
            proposal={proposal}
            form={proposalForm}
            field={field}
            key={idx}
          />
        ))}
      </div>
    </div>
  );
}

function ProposalField({
  proposal,
  form,
  field,
}: {
  proposal: Proposal;
  form: ProposalForm;
  field: Field;
}) {
  const response = proposal.fieldResponses[field.id];

  return (
    <div className="flex flex-col">
      <FormField field={field} />
      {response != null ? (
        <FieldResponseEl
          proposal={proposal}
          response={response}
          fieldId={field.id}
          formId={form.id}
        />
      ) : (
        <span className="text-gray-500">(No response)</span>
      )}
    </div>
  );
}

function FieldResponseEl({
  proposal,
  response,
  fieldId,
  formId,
}: {
  proposal: Proposal;
  response: FieldResponse;
  fieldId: string;
  formId: string;
}) {
  const dispatch = useContext(DispatchContext);
  const [isSaved, setIsSaved] = useState(true);
  const [responseState, setResponseState] = useState(response);

  function onResponseChange(e: React.ChangeEvent<HTMLInputElement>) {
    const newValue = e.target.value;

    if (responseState.type === "question") {
      if (newValue === responseState.value) {
        setIsSaved(true);
      } else {
        setIsSaved(false);
      }

      setResponseState({
        ...responseState,
        value: newValue,
      });
    } else {
      setResponseState({
        type: "question",
        fieldId: responseState.fieldId,
        value: newValue,
      });
      setIsSaved(false);
    }
  }

  function updateResponse() {
    void dispatch({
      type: "PROPOSAL_FIELD_RESPONSE_CHANGE",
      proposalId: proposal.id,
      proposalFormId: formId,
      fieldId,
      response: responseState,
    });

    setIsSaved(true);
  }

  switch (responseState.type) {
    case "question":
      return (
        <>
          <input
            className="dark:bg-[#333] border border-[#444] rounded px-1 py-1 text-xs focus:outline-none focus:border-[#2185d0] focus:shadow-[inset_0_-100px_0_rgba(33,133,208,0.15)]"
            value={responseState.value}
            type="text"
            onChange={onResponseChange}
          />
          {isSaved ? (
            ""
          ) : (
            <div className="mt-2">
              <Button onClick={updateResponse}>Save</Button>
            </div>
          )}
        </>
      );
    case "error":
      return <div className="text-red-500">{responseState.error}</div>;
    default:
      assertUnreachable(responseState);
  }
}
